import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosRDV } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

const rejectMsg = (err, fallback) => ({
  message: err?.response?.data?.message || err?.message || fallback,
});

const normalizeStrId = (val) => {
  if (val == null) return null;
  if (typeof val === "string") return val.trim() || null;
  if (typeof val === "number") return String(val);
  if (typeof val === "object") {
    const id = val.externalId || val.uuid || val._id || val.id || val.userId;
    return id != null ? String(id).trim() || null : null;
  }
  return null;
};

/* ===================== CREATE ===================== */
export const createRendezvous = createAsyncThunk(
  "rendezvous/create",
  async ({ patientId, medecinId, payload, asPatient = false }, { rejectWithValue }) => {
    try {
      const pId = normalizeStrId(patientId);
      const mId = normalizeStrId(medecinId);
      if (!pId) return rejectWithValue({ message: "patientId is required" });
      if (!mId) return rejectWithValue({ message: "medecinId is required" });
      const url = asPatient
        ? ENDPOINTS.RENDEZVOUS.CREATE_BY_PATIENT(pId, mId)
        : ENDPOINTS.RENDEZVOUS.CREATE(pId, mId);
      const { data } = await axiosRDV.post(url, payload);
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to create rendezvous"));
    }
  }
);

/* ===================== UPDATE ===================== */
export const updateRendezvous = createAsyncThunk(
  "rendezvous/update",
  async ({ userId, id, payload }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      if (!uId) return rejectWithValue({ message: "userId is required" });
      const url = ENDPOINTS.RENDEZVOUS.UPDATE(uId, id);
      const { data } = await axiosRDV.patch(url, payload);
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to update rendezvous"));
    }
  }
);

/* ===================== CANCEL ===================== */
export const cancelRendezvous = createAsyncThunk(
  "rendezvous/cancel",
  async ({ userId, rendezvousId, by = "doctor" }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      const rId = normalizeStrId(rendezvousId);

      // ✅ FIX — guards AVANT la construction de l'URL
      if (!uId) return rejectWithValue({ message: "userId is required" });
      if (!rId) return rejectWithValue({ message: "rendezvousId is required" });

      let url;
      if (by === "patient") {
        url = ENDPOINTS.RENDEZVOUS.CANCEL_BY_PATIENT(uId, rId);
      } else {
        url = ENDPOINTS.RENDEZVOUS.CANCEL_BY_DOCTOR(uId, rId);
      }


      const { data } = await axiosRDV.post(url, {}, {
        headers: { "Content-Type": "application/json" },
      });

      const returned = data?.data ?? data;
      const id = returned?.id ?? returned?.rendezvousId ?? returned?._id ?? rId;

      return {
        id:          id != null ? String(id) : String(rId),
        updated:     returned,
        cancelledBy: returned?.cancelledBy || by.toUpperCase(),
      };
    } catch (err) {
      console.error("🔴 [cancelRendezvous] ERREUR:", err?.response?.status, err?.response?.data || err?.message);
      return rejectWithValue(
        err?.response?.data || { message: err?.message || "Cancel failed" }
      );
    }
  }
);

/* ===================== CANCEL WRAPPERS ===================== */
export const cancelRendezvousByDoctor = createAsyncThunk(
  "rendezvous/cancelByDoctor",
  async ({ doctorId, rendezvousId }, thunkApi) =>
    thunkApi
      .dispatch(cancelRendezvous({ userId: doctorId, rendezvousId, by: "doctor" }))
      .unwrap()
);

export const cancelRendezvousByPatient = createAsyncThunk(
  "rendezvous/cancelByPatient",
  async ({ patientId, rendezvousId }, thunkApi) =>
    thunkApi
      .dispatch(cancelRendezvous({ userId: patientId, rendezvousId, by: "patient" }))
      .unwrap()
);

/* ===================== RESCHEDULE ===================== */
export const rescheduleRendezvous = createAsyncThunk(
  "rendezvous/reschedule",
  async (
    { userId, rendezvousId, startTime, endTime },
    { rejectWithValue }
  ) => {
    try {
      const uId = normalizeStrId(userId);
      const rId = normalizeStrId(rendezvousId);

      if (!uId)       return rejectWithValue({ message: "userId is required" });
      if (!rId)       return rejectWithValue({ message: "rendezvousId is required" });
      if (!startTime) return rejectWithValue({ message: "startTime is required" });
      if (!endTime)   return rejectWithValue({ message: "endTime is required" });

      const url = ENDPOINTS.RENDEZVOUS.RESCHEDULE(uId, rId);


      const { data } = await axiosRDV.patch(url, {
        startTime,
        endTime,
        status: "rescheduled", // ✅ AJOUT CRITIQUE
      });

      return data || {
        id: rId,
        startTime,
        endTime,
        status: "rescheduled",
      };

    } catch (err) {
      console.error("❌ RESCHEDULE ERROR:", err?.response?.data || err.message);

      return rejectWithValue(
        err?.response?.data || {
          message: err?.message || "Failed to reschedule",
        }
      );
    }
  }
);

/* ===================== CONFIRM ===================== */
export const confirmRendezvous = createAsyncThunk(
  "rendezvous/confirm",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      if (!uId) return rejectWithValue({ message: "userId is required" });
      const url = ENDPOINTS.RENDEZVOUS.CONFIRM(uId, id);
      const { data } = await axiosRDV.patch(url);
      return { id, data };
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to confirm rendezvous"));
    }
  }
);

/* ===================== LISTS ===================== */
export const fetchAllRendezvous = createAsyncThunk(
  "rendezvous/fetchAll",
  async (query = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosRDV.get(ENDPOINTS.RENDEZVOUS.BASE(), { params: query });
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch rendezvous"));
    }
  }
);

export const fetchRendezvousByMedecin = createAsyncThunk(
  "rendezvous/fetchByMedecin",
  async ({ userId, query = {} }, { rejectWithValue }) => {
    try {
      const mId = normalizeStrId(userId);
      if (!mId) return rejectWithValue({ message: "userId (medecinId) is required" });
      const { data } = await axiosRDV.get(
        ENDPOINTS.RENDEZVOUS.BY_MEDECIN(mId),
        { params: { ...query } }
      );
      return Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch rendezvous by medecin"));
    }
  }
);

export const fetchPatientRendezvousByExternalId = createAsyncThunk(
  "rendezvous/fetchByPatientExternalId",
  async ({ userId, query = {} }, { rejectWithValue }) => {
    try {
      const pId = normalizeStrId(userId);
      if (!pId) return rejectWithValue({ message: "userId (patientId) is required" });
      const { data } = await axiosRDV.get(
        ENDPOINTS.RENDEZVOUS.BY_PATIENT_EXTERNAL(pId),
        { params: query }
      );
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch patient rendezvous"));
    }
  }
);

export const fetchConfirmedRendezvous = createAsyncThunk(
  "rendezvous/fetchConfirmed",
  async ({ userId, period }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      if (!uId) return rejectWithValue({ message: "userId is required" });
      const { data } = await axiosRDV.get(ENDPOINTS.RENDEZVOUS.CONFIRMED(uId, period));
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch confirmed rendezvous"));
    }
  }
);

export const fetchScheduledRendezvous = createAsyncThunk(
  "rendezvous/fetchScheduled",
  async ({ userId, period }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      if (!uId) return rejectWithValue({ message: "userId is required" });
      const { data } = await axiosRDV.get(ENDPOINTS.RENDEZVOUS.SCHEDULED(uId, period));
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch scheduled rendezvous"));
    }
  }
);

export const fetchRescheduledRendezvous = createAsyncThunk(
  "rendezvous/fetchRescheduled",
  async ({ userId, period }, { rejectWithValue }) => {
    try {
      const uId = normalizeStrId(userId);
      if (!uId) return rejectWithValue({ message: "userId is required" });
      const { data } = await axiosRDV.get(ENDPOINTS.RENDEZVOUS.RESCHEDULED(uId, period));
      return data;
    } catch (err) {
      return rejectWithValue(rejectMsg(err, "Failed to fetch rescheduled rendezvous"));
    }
  }
);